import { CourseFormFieldApi } from "api";
import { CourseTrancheApi } from "api/types";
import { REGISTRATION_TRANCHE_PAYMENT_STATUSES, RegistrationTrancheStatus } from "consts";
import { CourseStudentApi, CourseStudentTrancheApi } from "api/courseStudentsApi";
import { TanTableColumn } from "components/molecules";

export type StudentColumnDef = TanTableColumn<CourseStudentApi> & {
  accessorKey: string;
};

export const TrancheStatusClasses: Record<RegistrationTrancheStatus, string> = {
  [REGISTRATION_TRANCHE_PAYMENT_STATUSES.NOT_PAID]: "text-red-600",
  [REGISTRATION_TRANCHE_PAYMENT_STATUSES.PENDING_PAYMENT]: "text-yellow-600",
  [REGISTRATION_TRANCHE_PAYMENT_STATUSES.PAID_BY_USER]: "text-green-600",
  [REGISTRATION_TRANCHE_PAYMENT_STATUSES.SKIPPED_BY_ADMIN]: "text-blue-600",
  [REGISTRATION_TRANCHE_PAYMENT_STATUSES.PAYED_OUTSIDE_SYSTEM]: "text-green-700",
};

export const baseUserProps = [
  { value: "lp", label: "Lp" },
  { value: "id", label: "ID" },
  { value: "fullName", label: "Imię i nazwisko" },
  { value: "firstName", label: "Imię" },
  { value: "surname", label: "Nazwisko" },
  { value: "email", label: "Email" },
  { value: "pesel", label: "PESEL" },
  { value: "phone", label: "Telefon" },
  { value: "address", label: "Adres" },
  { value: "bornPlace", label: "Miejsce urodzenia" },
  { value: "bornDate", label: "Data urodzenia", type: "date" },
];

export const paymentProps = [
  { value: "fullyPaid", label: "Opłacone", type: "boolean" },
  { value: "isInvoice", label: "Faktura", type: "boolean" },
  { value: "buyer", label: "Nabywca" },
  { value: "buyerAddress", label: "Adres nabywcy" },
  { value: "buyerNIP", label: "NIP nabywcy" },
  { value: "isUrzadPracy", label: "Urzad pracy" },
  { value: "urzadPracyName", label: "Nazwa urzędu pracy" },
  { value: "discountCode", label: "Kod rabatowy" },
  { value: "additionalTranches", label: "Dodatkowe transze" },
  { value: "totalPaid", label: "Całość zapłacone" },
  { value: "totalUnpaid", label: "Całość nieopłacone" },
];

export const registrationProps = [{ value: "registrationDate", label: "Data rejestracji", type: "date-with-time" }];

export const notesProps = [
  { value: "allNotes", label: "Notatki" },
  { value: "allNotesWithDetails", label: "Notatki + autor" },
];

export type ColumnKind =
  | "plain"
  | "boolean"
  | "date"
  | "date-with-time"
  | "name"
  | "paid"
  | "tranche"
  | "additionalTranches"
  | "totalPaid"
  | "totalUnpaid"
  | "notes"
  | "customField";

export interface ColumnMeta {
  key: string;
  label: string;
  kind: ColumnKind;
  trancheId?: number;
}

// Describes how a field key (as used by baseUserProps/paymentProps/registrationProps/notesProps
// and the tranche_<id>/customField keys) should be rendered, so both the old flat table and the
// v2 styled table agree on what each column means.
export const getColumnMeta = (prop: string, fields: CourseFormFieldApi[], tranches: CourseTrancheApi[]): ColumnMeta | null => {
  if (prop === "fullName") return { key: prop, label: "Kursant", kind: "name" };
  if (prop === "fullyPaid") return { key: prop, label: "Status", kind: "paid" };
  if (prop === "allNotes" || prop === "allNotesWithDetails") {
    const meta = notesProps.find((x) => x.value === prop);
    return { key: prop, label: meta?.label ?? prop, kind: "notes" };
  }
  if (prop === "additionalTranches") return { key: prop, label: "Dodatkowe transze", kind: "additionalTranches" };
  if (prop === "totalPaid") return { key: prop, label: "Całość zapłacone", kind: "totalPaid" };
  if (prop === "totalUnpaid") return { key: prop, label: "Całość nieopłacone", kind: "totalUnpaid" };
  if (prop.startsWith("tranche_")) {
    const trancheId = Number(prop.replace("tranche_", ""));
    const tranche = tranches.find((x) => x.id === trancheId);
    if (!tranche) return null;
    return { key: prop, label: tranche.name, kind: "tranche", trancheId };
  }
  const flatMeta = [...baseUserProps, ...paymentProps, ...registrationProps].find((x) => x.value === prop);
  if (flatMeta) {
    const kind: ColumnKind = flatMeta.type === "boolean" ? "boolean" : flatMeta.type === "date" || flatMeta.type === "date-with-time" ? (flatMeta.type as ColumnKind) : "plain";
    return { key: prop, label: flatMeta.label, kind };
  }
  const formField = fields.find((x) => x.id.toString() === prop);
  if (formField) return { key: prop, label: formField.name, kind: "customField" };
  return null;
};

const PAID_TRANCHE_STATUSES: RegistrationTrancheStatus[] = [
  REGISTRATION_TRANCHE_PAYMENT_STATUSES.PAID_BY_USER,
  REGISTRATION_TRANCHE_PAYMENT_STATUSES.PAYED_OUTSIDE_SYSTEM,
];
const UNPAID_TRANCHE_STATUSES: RegistrationTrancheStatus[] = [
  REGISTRATION_TRANCHE_PAYMENT_STATUSES.NOT_PAID,
  REGISTRATION_TRANCHE_PAYMENT_STATUSES.PENDING_PAYMENT,
];

const sumTranchesByStatus = (tranches: CourseStudentTrancheApi[], statuses: RegistrationTrancheStatus[]) =>
  tranches.filter((t) => statuses.includes(t.status)).reduce((sum, t) => sum + t.discountedAmount, 0);

export const getTotalPaid = (tranches: CourseStudentTrancheApi[]) => sumTranchesByStatus(tranches, PAID_TRANCHE_STATUSES);
export const getTotalUnpaid = (tranches: CourseStudentTrancheApi[]) => sumTranchesByStatus(tranches, UNPAID_TRANCHE_STATUSES);

export const customMapStudents = (students: CourseStudentApi[], total: number, courseTranches: CourseTrancheApi[]) => {
  const courseTrancheIds = new Set(courseTranches.map((t) => t.id));

  return {
    total: total,
    data: students.map((student, idx) => {
      const { forms, firstName, surname, tranches, notes, ...restOfStudent } = student;

      const tranchesObject = tranches.reduce(
        (acc, tranche) => {
          acc[`tranche_${tranche.courseTrancheId}`] = tranche;
          return acc;
        },
        {} as Record<string, (typeof tranches)[number]>,
      );

      const additionalTranches = tranches.filter((tranche) => !courseTrancheIds.has(tranche.courseTrancheId));

      const objectFields =
        forms.length > 0
          ? forms.reduce((acc, field) => {
              // @ts-ignore
              acc[field.courseFormFieldId] = field.value;
              return acc;
            }, {})
          : {};

      const formattedNotes = notes && notes.length > 0 ? notes.map((note) => note.notes).join(" | ") : "";

      const formattedNotesWithDetails =
        notes && notes.length > 0
          ? notes
              .map(
                (note) =>
                  `${note.notes} (${note.authorFullName}, ${new Date(note.createdDate).toLocaleDateString("pl-PL", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })})`,
              )
              .join(" | ")
          : "";

      return {
        lp: idx + 1,
        fullName: `${firstName} ${surname}`,
        firstName,
        ...tranchesObject,
        surname,
        tranches,
        additionalTranches,
        notes,
        allNotes: formattedNotes,
        allNotesWithDetails: formattedNotesWithDetails,
        totalPaid: getTotalPaid(tranches),
        totalUnpaid: getTotalUnpaid(tranches),
        ...restOfStudent,
        ...objectFields,
      };
    }),
  };
};
